import type { CountryCode } from '@klear/constants/countries'
import { Trader } from '@klear/trader/trader'
import type { TraderCreateWire, TraderUpdateWire } from '@klear/trader/trader.wire'
import isObject from 'lodash/isObject'
import toPairs from 'lodash/toPairs'

export type TraderFormErrorMap = Record<string, string>

const ORGANIZATION_WIRE_FIELDS = new Set([
  'name',
  'description',
  'size',
  'website',
  'dunsNumber',
  'lei',
  'ein',
  'vatNumber',
  'parentOrganization'
])

export function traderWireValidationToFormErrors(wireErrors: Record<string, string | undefined>): TraderFormErrorMap {
  const formErrors: TraderFormErrorMap = {}
  for (const [path, message] of toPairs(wireErrors)) {
    if (!message) continue
    if (path === 'root') {
      formErrors.root = message
      continue
    }
    if (path === 'address.country' || path.startsWith('address.')) {
      const addressField = path === 'address.country' ? 'country' : path.slice('address.'.length)
      formErrors[`organization.${addressField}`] = message
      continue
    }
    if (ORGANIZATION_WIRE_FIELDS.has(path)) {
      formErrors[`organization.${path}`] = message
      continue
    }
    formErrors[path] = message
  }
  return formErrors
}

export type TraderOrganizationForm = {
  id?: number
  country: CountryCode | ''
  name: string
  description: string
  size: string
  website: string
  dunsNumber: string
  lei: string
  ein: string
  vatNumber: string
  parentOrganization: string
}

export const EMPTY_TRADER_ORGANIZATION_FORM: TraderOrganizationForm = {
  country: '',
  name: '',
  description: '',
  size: '',
  website: '',
  dunsNumber: '',
  lei: '',
  ein: '',
  vatNumber: '',
  parentOrganization: ''
}

export type TraderForm = {
  id?: number
  organization: TraderOrganizationForm
  logoName: string
  logoDownloadKey: string
  logoUrl: string
  hasFx: boolean
}

export type TraderFormDirty = Partial<Record<Exclude<keyof TraderForm, 'organization'>, boolean | object>> & {
  organization?: boolean | Partial<Record<keyof TraderOrganizationForm, boolean>>
}

export const EMPTY_TRADER_FORM: TraderForm = {
  organization: EMPTY_TRADER_ORGANIZATION_FORM,
  logoName: '',
  logoDownloadKey: '',
  logoUrl: '',
  hasFx: false
}

function optionalText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function optionalTextFields<const K extends string>(
  fields: Record<K, string | null | undefined>
): Partial<Record<K, string>> {
  const wire: Partial<Record<K, string>> = {}
  for (const key in fields) {
    const value = optionalText(fields[key])
    if (value !== undefined) wire[key] = value
  }
  return wire
}

function clearableText(value: string | null | undefined): string | null {
  return optionalText(value) ?? null
}

function isDirty(dirty: TraderFormDirty, key: Exclude<keyof TraderForm, 'organization'>): boolean {
  return dirty[key] === true || (dirty[key] != null && isObject(dirty[key]))
}

function isOrganizationDirty(dirty: TraderFormDirty, key: keyof TraderOrganizationForm): boolean {
  return dirty.organization === true || (isObject(dirty.organization) && dirty.organization[key] === true)
}

function organizationFormToAddress(form: TraderOrganizationForm): TraderCreateWire['address'] {
  return { country: form.country as CountryCode }
}

function organizationFormToCreateSlice(
  form: TraderOrganizationForm
): Pick<
  TraderCreateWire,
  | 'name'
  | 'description'
  | 'address'
  | 'size'
  | 'website'
  | 'dunsNumber'
  | 'lei'
  | 'ein'
  | 'vatNumber'
  | 'parentOrganization'
> {
  return {
    name: form.name.trim(),
    address: organizationFormToAddress(form),
    size: form.size ? (form.size as TraderCreateWire['size']) : undefined,
    ...optionalTextFields({
      description: form.description,
      website: form.website,
      dunsNumber: form.dunsNumber,
      lei: form.lei,
      ein: form.ein,
      vatNumber: form.vatNumber,
      parentOrganization: form.parentOrganization
    })
  }
}

export function traderFormToCreateWire(form: TraderForm): TraderCreateWire {
  return {
    ...organizationFormToCreateSlice(form.organization),
    hasFx: form.hasFx,
    ...optionalTextFields({
      logoName: form.logoName,
      logoDownloadKey: form.logoDownloadKey,
      logoUrl: form.logoUrl
    })
  }
}

export function traderFormToUpdateWire(form: TraderForm, dirty: TraderFormDirty): TraderUpdateWire {
  const org = form.organization
  const wire: TraderUpdateWire = {}

  if (isOrganizationDirty(dirty, 'country')) {
    wire.address = { country: org.country as CountryCode }
  }
  if (isOrganizationDirty(dirty, 'name')) wire.name = org.name
  if (isOrganizationDirty(dirty, 'description')) wire.description = clearableText(org.description)
  if (isOrganizationDirty(dirty, 'size')) {
    wire.size = (org.size === '' ? null : org.size) as TraderUpdateWire['size']
  }
  if (isOrganizationDirty(dirty, 'website')) wire.website = clearableText(org.website)
  if (isOrganizationDirty(dirty, 'dunsNumber')) wire.dunsNumber = clearableText(org.dunsNumber)
  if (isOrganizationDirty(dirty, 'lei')) wire.lei = clearableText(org.lei)
  if (isOrganizationDirty(dirty, 'ein')) wire.ein = clearableText(org.ein)
  if (isOrganizationDirty(dirty, 'vatNumber')) wire.vatNumber = clearableText(org.vatNumber)
  if (isOrganizationDirty(dirty, 'parentOrganization')) {
    wire.parentOrganization = clearableText(org.parentOrganization)
  }
  if (isDirty(dirty, 'logoName')) wire.logoName = clearableText(form.logoName)
  if (isDirty(dirty, 'logoDownloadKey')) wire.logoDownloadKey = clearableText(form.logoDownloadKey)
  if (isDirty(dirty, 'logoUrl')) wire.logoUrl = clearableText(form.logoUrl)
  if (isDirty(dirty, 'hasFx')) wire.hasFx = form.hasFx

  return wire
}

export function traderReadToForm(trader: Trader): TraderForm {
  const address = trader.address
  return {
    id: trader.id > 0 ? trader.id : undefined,
    organization: {
      id: trader.organizationId > 0 ? trader.organizationId : undefined,
      country: address.country ?? '',
      name: trader.name ?? '',
      description: trader.description ?? '',
      size: trader.size ?? '',
      website: trader.website ?? '',
      dunsNumber: trader.dunsNumber ?? '',
      lei: trader.lei ?? '',
      ein: trader.ein ?? '',
      vatNumber: trader.vatNumber ?? '',
      parentOrganization: trader.parentOrganization ?? ''
    },
    logoName: trader.logoName,
    logoDownloadKey: trader.logoDownloadKey,
    logoUrl: trader.logoUrl,
    hasFx: trader.hasFx
  }
}
